import { useEffect, useState, useCallback, type CSSProperties } from "react";
import { styled, applyClassName } from "@glitz/react";
import { GrLinkPrevious } from "react-icons/gr";
import {
  spacing,
  black,
  green,
  white,
  grey,
  darkGrey,
  lightGrey,
  fontSize,
  fontWeight,
} from "../theme";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "";

type TrackingStatusEnum =
  | "not_available"
  | "delivered"
  | "in_transit"
  | "available_for_delivery"
  | "created"
  | "unknown";

interface TrackingEvent {
  time: string | null;
  description: string;
  location: string;
}

interface TrackingSuccessBody {
  status: Exclude<TrackingStatusEnum, "not_available">;
  statusText?: string;
  trackingNumber: string;
  carrier?: string;
  estimatedDelivery?: string | null;
  consignee?: { city: string; postalCode: string } | null;
  events?: TrackingEvent[];
  trackingUrl?: string;
  orderId?: number;
  cached?: boolean;
  wooOrderStatus?: string;
  orderCreatedAt?: string | null;
  orderPaidAt?: string | null;
  orderCompletedAt?: string | null;
  orderUpdatedAt?: string | null;
}

interface TrackingNotAvailableBody {
  status: "not_available";
  trackingNumber: null;
  orderId?: number;
  wooOrderStatus?: string;
  orderStatusExplanation?: string;
  orderCreatedAt?: string | null;
  orderPaidAt?: string | null;
  orderCompletedAt?: string | null;
  orderUpdatedAt?: string | null;
}

type TrackingPayload = TrackingSuccessBody | TrackingNotAvailableBody;

/** Ordern är inte completed i Woo — ingen PostNord-data än. */
interface PreShipmentPayload {
  phase: "pre_shipment";
  wooOrderStatus: string;
  orderStatusExplanation: string;
  orderId: number | string;
  hasTrackingNumberPending?: boolean;
  orderCreatedAt?: string | null;
  orderPaidAt?: string | null;
  orderCompletedAt?: string | null;
  orderUpdatedAt?: string | null;
}

interface LocalDeliveryPayload {
  phase: "local_delivery";
  status: "delivered";
  statusText?: string;
  trackingNumber: string;
  localOrderId: string;
  deliveryImageUrl: string;
  orderId?: number;
  wooOrderStatus?: string;
  orderCreatedAt?: string | null;
  orderPaidAt?: string | null;
  orderCompletedAt?: string | null;
  orderUpdatedAt?: string | null;
}

type TrackingApiPayload = TrackingPayload | PreShipmentPayload | LocalDeliveryPayload;

function isNotAvailable(
  b: TrackingApiPayload
): b is TrackingNotAvailableBody {
  return "status" in b && b.status === "not_available";
}

function isPreShipment(b: TrackingApiPayload | null): b is PreShipmentPayload {
  return b != null && "phase" in b && b.phase === "pre_shipment";
}

function isLocalDelivery(
  b: TrackingApiPayload | null
): b is LocalDeliveryPayload {
  return b != null && "phase" in b && b.phase === "local_delivery";
}

function hasPostNordDetails(b: TrackingApiPayload | null): b is TrackingSuccessBody {
  return (
    b != null &&
    !isPreShipment(b) &&
    !isLocalDelivery(b) &&
    "status" in b &&
    b.status !== "not_available" &&
    typeof (b as TrackingSuccessBody).trackingNumber === "string"
  );
}

type TimelineEvent = { time: string | null; description: string; location?: string };

function buildOrderTimeline(data: TrackingApiPayload): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const status = (data as { wooOrderStatus?: string }).wooOrderStatus || "";
  const createdAt = "orderCreatedAt" in data ? data.orderCreatedAt || null : null;
  const paidAt = "orderPaidAt" in data ? data.orderPaidAt || null : null;
  const completedAt = "orderCompletedAt" in data ? data.orderCompletedAt || null : null;
  const updatedAt = "orderUpdatedAt" in data ? data.orderUpdatedAt || null : null;

  events.push({ time: createdAt, description: "Order mottagen" });

  if (status === "on-hold") {
    events.push({ time: updatedAt, description: "Verifierar betalningen" });
  }

  if (status === "processing" || status === "completed") {
    events.push({ time: paidAt || updatedAt, description: "Ordern hanteras och packas på lagret" });
  }

  if (status === "completed") {
    events.push({
      time: completedAt || updatedAt,
      description: "Ordern är slutförd och överlämnad till transportören",
    });
  }

  return events;
}

const TrackingPage = () => {
  const navigate = useNavigate();
  const { orderRef } = useParams<{ orderRef: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TrackingApiPayload | null>(null);

  const fetchTracking = useCallback(async () => {
    if (!orderRef) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/orders/${encodeURIComponent(orderRef)}/tracking`
      );
      const json = await res.json().catch(() => ({}));

      if (res.status === 404) {
        setError(json.error || "Ordern hittades inte.");
        setData(null);
        return;
      }

      if (res.status === 503) {
        setError(
          json.error ||
            "Spårning är inte aktiverad på servern ännu."
        );
        setData(null);
        return;
      }

      if (res.status === 501) {
        setError(json.error || "Denna leverantör stöds inte ännu.");
        setData(null);
        return;
      }

      if (!res.ok && res.status !== 502) {
        setError(json.error || "Kunde inte hämta spårning.");
        setData(null);
        return;
      }

      setData(json as TrackingApiPayload);

      if (res.status === 502) {
        setError(
          json.error ||
            "PostNord kunde inte nås just nu. Försök igen eller öppna länken nedan."
        );
      }
    } catch {
      setError("Nätverksfel. Försök igen senare.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orderRef]);

  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  const statusBadge =
    data && hasPostNordDetails(data) ? data.status : null;

  return (
    <PageWrapper>
      <PageHeader>
        <IconWrapper onClick={() => navigate("/")}>
          <Icon />
          Tillbaka till shoppen
        </IconWrapper>
        <ImgWrapper>
          <Img
            src={`${process.env.PUBLIC_URL || ""}/trirebels_logo.png`}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </ImgWrapper>
        <Right />
      </PageHeader>

      <PageTitle>Spåra leverans</PageTitle>
      {orderRef && (
        <SubTitle>
          Orderreferens: <Mono>{orderRef}</Mono>
        </SubTitle>
      )}

      {loading && <StatusMessage>Hämtar order och spårning…</StatusMessage>}

      {!loading && error && !data && (
        <StatusMessage>
          <ErrorText>{error}</ErrorText>
        </StatusMessage>
      )}

      {!loading && data && isPreShipment(data) && (
        <ContentWrapper>
          <SectionTitle>Orderhändelser</SectionTitle>
          <StatusGroup>
            <StatusSectionKicker>Orderstatus</StatusSectionKicker>
            <StatusBannerOuter
              style={wooOrderStatusBannerStyle(data.wooOrderStatus)}
            >
              <StatusBannerLabel>
                {wooStatusLabelSv(data.wooOrderStatus)}
              </StatusBannerLabel>
            </StatusBannerOuter>
          </StatusGroup>
          <InfoCard>
            <InfoText>{data.orderStatusExplanation}</InfoText>
            {data.hasTrackingNumberPending ? (
              <PendingTrackingNote>
                Ett spårningsnummer finns redan på ordern — PostNord-uppgifter
                visas automatiskt när ordern markerats som slutförd i shoppen.
              </PendingTrackingNote>
            ) : null}
            {data.orderId != null && (
              <OrderHint>Order #{data.orderId}</OrderHint>
            )}
          </InfoCard>
          <OrderTimelineSection>
            <StatusSectionKicker>Ordertidslinje</StatusSectionKicker>
            <Timeline>
              {buildOrderTimeline(data).map((ev, idx) => (
                <TimelineRow key={`woo-${idx}`}>
                  <TimelineTime>{formatEventTime(ev.time)}</TimelineTime>
                  <TimelineBody>
                    <TimelineDesc>{ev.description}</TimelineDesc>
                    {ev.location ? <TimelineLoc>{ev.location}</TimelineLoc> : null}
                  </TimelineBody>
                </TimelineRow>
              ))}
            </Timeline>
          </OrderTimelineSection>
          <SectionTitle>Transporthändelser</SectionTitle>
          <InfoCard>
            <InfoText>
              Transporthändelser visas här när ordern markerats som slutförd och
              PostNord-data har hämtats.
            </InfoText>
          </InfoCard>
        </ContentWrapper>
      )}

      {!loading && data && isLocalDelivery(data) && (
        <ContentWrapper>
          <SectionTitle>Orderhändelser</SectionTitle>
          {data.wooOrderStatus ? (
            <StatusGroup>
              <StatusSectionKicker>Orderstatus</StatusSectionKicker>
              <StatusBannerOuter
                style={wooOrderStatusBannerStyle(data.wooOrderStatus)}
              >
                <StatusBannerLabel>
                  {wooStatusLabelSv(data.wooOrderStatus)}
                </StatusBannerLabel>
              </StatusBannerOuter>
            </StatusGroup>
          ) : null}
          <OrderTimelineSection>
            <StatusSectionKicker>Ordertidslinje</StatusSectionKicker>
            <Timeline>
              {buildOrderTimeline(data).map((ev, idx) => (
                <TimelineRow key={`woo-local-${idx}`}>
                  <TimelineTime>{formatEventTime(ev.time)}</TimelineTime>
                  <TimelineBody>
                    <TimelineDesc>{ev.description}</TimelineDesc>
                    {ev.location ? <TimelineLoc>{ev.location}</TimelineLoc> : null}
                  </TimelineBody>
                </TimelineRow>
              ))}
              <TimelineRow key="local-delivered">
                <TimelineTime>
                  {formatEventTime(data.orderCompletedAt || data.orderUpdatedAt || null)}
                </TimelineTime>
                <TimelineBody>
                  <TimelineDesc>Levererad lokalt av Trirebels</TimelineDesc>
                </TimelineBody>
              </TimelineRow>
            </Timeline>
          </OrderTimelineSection>

          <SectionTitle>Lokal leverans</SectionTitle>
          <StatusGroup>
            <StatusSectionKicker>Leveransstatus</StatusSectionKicker>
            <StatusBannerOuter
              style={{
                backgroundColor: STATUS_PALETTE.delivered.bg,
                borderLeft: `4px solid ${STATUS_PALETTE.delivered.border}`,
                marginBottom: 0,
              }}
            >
              <StatusBannerLabel>Levererad</StatusBannerLabel>
              <StatusBannerText>
                {(data.statusText || "").trim() || "Paketet är levererat lokalt."}
              </StatusBannerText>
            </StatusBannerOuter>
          </StatusGroup>

          <InfoCard>
            <InfoText>
              Vi försöker visa leveransbild för order <Mono>{data.localOrderId}</Mono>.
            </InfoText>
            <DeliveryProofImage
              src={data.deliveryImageUrl}
              alt={`Leveransbild för order ${data.localOrderId}`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <OrderHint>
              Om bilden inte visas än kan den vara på väg att laddas upp.
            </OrderHint>
          </InfoCard>
        </ContentWrapper>
      )}

      {!loading && data && isNotAvailable(data) && (
        <ContentWrapper>
          <SectionTitle>Orderhändelser</SectionTitle>
          {data.wooOrderStatus ? (
            <StatusGroup>
              <StatusSectionKicker>Orderstatus</StatusSectionKicker>
              <StatusBannerOuter
                style={wooOrderStatusBannerStyle(data.wooOrderStatus)}
              >
                <StatusBannerLabel>
                  {wooStatusLabelSv(data.wooOrderStatus)}
                </StatusBannerLabel>
              </StatusBannerOuter>
            </StatusGroup>
          ) : null}
          <InfoCard>
            <InfoTitle>Ingen spårning ännu</InfoTitle>
            <InfoText>
              {(data.orderStatusExplanation || "").trim() ||
                "När paketet skickats läggs ett spårningsnummer till er order. Kom tillbaka hit senare eller kontakta oss om ni har frågor."}
            </InfoText>
            {data.orderId != null && (
              <OrderHint>Order #{data.orderId}</OrderHint>
            )}
          </InfoCard>
          <OrderTimelineSection>
            <StatusSectionKicker>Ordertidslinje</StatusSectionKicker>
            <Timeline>
              {buildOrderTimeline(data).map((ev, idx) => (
                <TimelineRow key={`woo-${idx}`}>
                  <TimelineTime>{formatEventTime(ev.time)}</TimelineTime>
                  <TimelineBody>
                    <TimelineDesc>{ev.description}</TimelineDesc>
                    {ev.location ? <TimelineLoc>{ev.location}</TimelineLoc> : null}
                  </TimelineBody>
                </TimelineRow>
              ))}
            </Timeline>
          </OrderTimelineSection>
          <SectionTitle>Transporthändelser</SectionTitle>
          <InfoCard>
            <InfoText>
              Ingen transportdata ännu. När ordern är slutförd och ett
              spårningsnummer finns visas PostNord-händelser här.
            </InfoText>
          </InfoCard>
        </ContentWrapper>
      )}

      {!loading &&
        data &&
        hasPostNordDetails(data) &&
        statusBadge && (
          <ContentWrapper>
            <SectionTitle>Orderhändelser</SectionTitle>
            {data.wooOrderStatus ? (
              <StatusGroup>
                <StatusSectionKicker>Orderstatus</StatusSectionKicker>
                <StatusBannerOuter
                  style={wooOrderStatusBannerStyle(data.wooOrderStatus)}
                >
                  <StatusBannerLabel>
                    {wooStatusLabelSv(data.wooOrderStatus)}
                  </StatusBannerLabel>
                </StatusBannerOuter>
              </StatusGroup>
            ) : null}
            <OrderTimelineSection>
              <StatusSectionKicker>Ordertidslinje</StatusSectionKicker>
              <Timeline>
                {buildOrderTimeline(data).map((ev, idx) => (
                  <TimelineRow key={`woo-${idx}`}>
                    <TimelineTime>{formatEventTime(ev.time)}</TimelineTime>
                    <TimelineBody>
                      <TimelineDesc>{ev.description}</TimelineDesc>
                      {ev.location ? <TimelineLoc>{ev.location}</TimelineLoc> : null}
                    </TimelineBody>
                  </TimelineRow>
                ))}
              </Timeline>
            </OrderTimelineSection>

            <SectionTitle>Transporthändelser</SectionTitle>
            <StatusGroup>
              <StatusSectionKicker>Fraktstatus</StatusSectionKicker>
              <StatusBannerOuter
                style={{
                  backgroundColor:
                    STATUS_PALETTE[statusBadge]?.bg ?? STATUS_PALETTE.unknown.bg,
                  borderLeft: `4px solid ${
                    STATUS_PALETTE[statusBadge]?.border ??
                    STATUS_PALETTE.unknown.border
                  }`,
                  marginBottom: 0,
                }}
              >
                <StatusBannerLabel>
                  {statusLabelSv(statusBadge)}
                </StatusBannerLabel>
                {(data.statusText || "").trim().length > 0 && (
                  <StatusBannerText>{data.statusText}</StatusBannerText>
                )}
              </StatusBannerOuter>
            </StatusGroup>

            <MetaGrid>
              <MetaItem>
                <MetaLabel>Spårningsnummer</MetaLabel>
                <Mono>{data.trackingNumber}</Mono>
              </MetaItem>
              {data.estimatedDelivery && (
                <MetaItem>
                  <MetaLabel>Beräknad leverans</MetaLabel>
                  <MetaValue>
                    {formatEtaDisplay(data.estimatedDelivery)}
                  </MetaValue>
                </MetaItem>
              )}
              {data.consignee &&
                (data.consignee.city || data.consignee.postalCode) && (
                  <MetaItem>
                    <MetaLabel>Mottagare (ort)</MetaLabel>
                    <MetaValue>
                      {[data.consignee.postalCode, data.consignee.city]
                        .filter(Boolean)
                        .join(" ")}
                    </MetaValue>
                  </MetaItem>
                )}
            </MetaGrid>

            {error && (
              <WarningBox>
                <ErrorText>{error}</ErrorText>
              </WarningBox>
            )}

            <Section>
              <StatusSectionKicker>Transporttidslinje</StatusSectionKicker>
              <Timeline>
                {data.events && data.events.length > 0 ? (
                  data.events.map((ev, idx) => (
                    <TimelineRow key={`pn-${ev.time}-${idx}`}>
                      <TimelineTime>{formatEventTime(ev.time)}</TimelineTime>
                      <TimelineBody>
                        <TimelineDesc>{ev.description}</TimelineDesc>
                        {ev.location ? (
                          <TimelineLoc>{ev.location}</TimelineLoc>
                        ) : null}
                      </TimelineBody>
                    </TimelineRow>
                  ))
                ) : (
                  <TimelineRow key="pn-empty">
                    <TimelineTime>—</TimelineTime>
                    <TimelineBody>
                      <TimelineDesc>Inga PostNord-händelser ännu.</TimelineDesc>
                    </TimelineBody>
                  </TimelineRow>
                )}
              </Timeline>
            </Section>

            {data.trackingUrl && (
              <ExternalSection>
                <ExternalLink href={data.trackingUrl} target="_blank" rel="noopener noreferrer">
                  Öppna på postnord.se
                </ExternalLink>
              </ExternalSection>
            )}
          </ContentWrapper>
        )}
    </PageWrapper>
  );
};

/** Samma mönster som frakt: ljus botten + vänsterkant (blå processing, guld on-hold, grön completed, …) */
const WOO_ORDER_BANNER_COLORS: Record<string, { bg: string; border: string }> = {
  processing: { bg: "#e3f2fd", border: "#64b5f6" },
  "on-hold": { bg: "#fff8e1", border: "#ffb74d" },
  completed: { bg: "#e8f5e9", border: "#81c784" },
  pending: { bg: "#eceff1", border: "#90a4ae" },
  cancelled: { bg: "#ffebee", border: "#e57373" },
  refunded: { bg: "#fff3e0", border: "#ffb74d" },
  failed: { bg: "#ffebee", border: "#e53935" },
};

function wooOrderStatusBannerStyle(wooStatus: string): CSSProperties {
  const c = WOO_ORDER_BANNER_COLORS[wooStatus] || {
    bg: "#f5f5f5",
    border: "#bdbdbd",
  };
  return {
    backgroundColor: c.bg,
    borderLeft: `4px solid ${c.border}`,
    marginBottom: 0,
  };
}

function wooStatusLabelSv(wooStatus: string): string {
  const map: Record<string, string> = {
    pending: "Inväntar betalning",
    "on-hold": "Verifierar betalning",
    processing: "Packas på lagret",
    completed: "Slutförd",
    cancelled: "Avbruten",
    refunded: "Återbetald",
    failed: "Misslyckad",
  };
  return map[wooStatus] || wooStatus;
}

function statusLabelSv(s: TrackingStatusEnum): string {
  const map: Record<string, string> = {
    delivered: "Levererad",
    in_transit: "På väg",
    available_for_delivery: "Kan hämtas ut",
    created: "Registrerad",
    unknown: "Okänd status",
    not_available: "",
  };
  return map[s] || s;
}

function formatEtaDisplay(raw: string): string {
  try {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("sv-SE", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }
  } catch {
    /* noop */
  }
  return raw;
}

const STATUS_PALETTE: Record<
  TrackingStatusEnum,
  { bg: string; border: string }
> = {
  not_available: { bg: "#f5f5f5", border: "#bdbdbd" },
  delivered: { bg: "#e8f5e9", border: "#81c784" },
  in_transit: { bg: "#e3f2fd", border: "#64b5f6" },
  available_for_delivery: { bg: "#fff8e1", border: "#ffb74d" },
  created: { bg: "#f5f5f5", border: "#bdbdbd" },
  unknown: { bg: "#fce4ec", border: "#f48fb1" },
};

function formatEventTime(time: string | null): string {
  if (!time) return "—";
  try {
    const d = new Date(time);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("sv-SE", {
        dateStyle: "short",
        timeStyle: "short",
      });
    }
  } catch {
    /* noop */
  }
  return time;
}

const PageWrapper = styled.div({
  minHeight: "100vh",
  marginBottom: spacing.xl,
});

const PageHeader = styled.div({
  display: "flex",
  padding: { x: spacing.xl, y: spacing.m },
  backgroundColor: white,
});

const IconWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  cursor: "pointer",
  width: "25%",
  color: black,
  ":hover": { color: green },
});

const Icon = styled(applyClassName(GrLinkPrevious), {
  fontSize: "30px",
});

const ImgWrapper = styled.div({
  width: "50%",
  display: "flex",
  justifyContent: "center",
});

const Img = styled.img({
  width: "75px",
  height: "75px",
  objectFit: "cover",
  padding: { xy: spacing.xs },
  cursor: "pointer",
});

const Right = styled.div({ width: "25%" });

const PageTitle = styled.h1({
  textAlign: "center",
  marginBottom: spacing.xs,
});

const SubTitle = styled.p({
  textAlign: "center",
  marginTop: 0,
  marginBottom: spacing.l,
  fontSize: fontSize.m,
  color: darkGrey,
});

const Mono = styled.span({
  fontFamily: "ui-monospace, monospace",
});

const StatusMessage = styled.div({
  textAlign: "center",
  marginTop: spacing.xxl,
  fontSize: fontSize.l,
});

const ErrorText = styled.span({ color: "#c00" });

const ContentWrapper = styled.div({
  maxWidth: "640px",
  margin: { x: "auto" },
  padding: { x: spacing.m },
});

const InfoCard = styled.div({
  padding: { xy: spacing.l },
  borderRadius: "8px",
  border: { xy: { style: "solid", width: "1px", color: lightGrey } },
  backgroundColor: "#fafafa",
});

const InfoTitle = styled.h2({
  margin: { top: 0, bottom: spacing.s },
  fontSize: fontSize.l,
});

const InfoText = styled.p({
  margin: { top: 0, bottom: spacing.m },
  fontSize: fontSize.m,
  lineHeight: 1.5,
});

const OrderHint = styled.p({
  margin: { top: 0, bottom: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
});

const StatusSectionKicker = styled.div({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: spacing.xs,
});

const StatusGroup = styled.div({
  marginBottom: spacing.l,
});

const PendingTrackingNote = styled.p({
  margin: { top: spacing.m, bottom: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: 1.5,
});

const OrderTimelineSection = styled.div({
  marginTop: spacing.l,
  marginBottom: spacing.l,
});

const StatusBannerOuter = styled.div({
  padding: { xy: spacing.m },
  borderRadius: "8px",
  marginBottom: spacing.l,
});

const StatusBannerLabel = styled.div({
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
});

const StatusBannerText = styled.div({
  marginTop: spacing.xs,
  fontSize: fontSize.m,
  color: darkGrey,
});

const MetaGrid = styled.div({
  display: "grid",
  gap: spacing.m,
  marginBottom: spacing.l,
});

const MetaItem = styled.div({});

const MetaLabel = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
  marginBottom: spacing.xxs,
});

const MetaValue = styled.div({
  fontSize: fontSize.m,
});

const WarningBox = styled.div({
  padding: { xy: spacing.m },
  marginBottom: spacing.l,
  borderRadius: "6px",
  backgroundColor: "#fff3e0",
});

const Section = styled.section({
  marginBottom: spacing.l,
});

const SectionTitle = styled.h2({
  fontSize: fontSize.l,
  marginBottom: spacing.m,
});

const Timeline = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 0,
});

const TimelineRow = styled.div({
  display: "grid",
  gridTemplateColumns: "130px 1fr",
  gap: spacing.m,
  padding: { y: spacing.s },
  borderBottom: { style: "solid", width: "1px", color: lightGrey },
});

const TimelineTime = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const TimelineBody = styled.div({});

const TimelineDesc = styled.div({
  fontSize: fontSize.m,
});

const TimelineLoc = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  marginTop: spacing.xxs,
});

const ExternalSection = styled.div({
  marginTop: spacing.l,
  textAlign: "center",
});

const ExternalLink = styled.a({
  fontSize: fontSize.m,
  color: green,
  fontWeight: fontWeight.regular,
});

const DeliveryProofImage = styled.img({
  width: "100%",
  maxWidth: "420px",
  height: "auto",
  borderRadius: "8px",
  border: { xy: { style: "solid", width: "1px", color: lightGrey } },
  marginBottom: spacing.s,
  backgroundColor: white,
});

export default TrackingPage;
